import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { cn } from "../utils/cn";
import { IoMdCloseCircleOutline } from "react-icons/io";
export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  theme?: "dark" | "light";
  width?: number; // caller-controlled
  height?: number; // caller-controlled
  className?: string; // caller-controlled
  closeOnOverlayClick?: boolean;
  showCloseButton?: boolean;
}

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  theme = "dark",
  width = 650,
  className,
  closeOnOverlayClick = true,
  showCloseButton = true,
}) => {
  const panelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!isOpen) return;

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };

    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const panelBase =
    theme === "dark"
      ? "bg-[#0F141A] backdrop-blur-xl border border-white/15 text-white"
      : "bg-white border border-black/10 text-black";

  if (typeof document === "undefined") return null;

  // Portal escapes any ancestor stacking context (transforms, filters,
  // will-change), so the modal always positions relative to the viewport
  // and isn't accidentally blurred by a parent with backdrop-filter.
  return createPortal(
    <div className="fixed inset-0 z-[1000] flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={closeOnOverlayClick ? onClose : undefined}
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        tabIndex={-1}
        style={{ width, maxWidth: "calc(100vw - 32px)" }}
        className={cn(
          "relative flex flex-col rounded-2xl shadow-[0_10px_40px_rgba(0,0,0,0.6)]",
          panelBase,
          className
        )}
      >
        <div
          className="flex items-center justify-between"
          style={{ padding: "20px 28px 12px 28px" }}
        >
          {title ? (
            <h2 className="text-lg font-semibold leading-tight">{title}</h2>
          ) : (
            <span />
          )}
          {showCloseButton && (
            <button
              onClick={onClose}
              className="text-white/60 hover:text-white"
              aria-label="Close"
              style={{ marginLeft: 12 }}
            >
              <IoMdCloseCircleOutline className="w-5 h-5" />
            </button>
          )}
        </div>
        <div style={{ padding: "8px 28px 24px 28px" }}>{children}</div>
      </div>
    </div>,
    document.body
  );
};
